import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import { queryClient } from '../lib/queryClient';
import { useCompanyStore } from './companyStore';
import { isInternalRole } from '../utils/roles.js';

/**
 * Authentication store using Zustand
 */
/**
 * Role hierarchy for permission checks
 */
let logoutInFlight = null;

const ROLE_HIERARCHY = {
  end_user: 0,
  team_member: 0,
  billing: 0,
  company_admin: 1,
  finance: 2,        // Can apply payments, view all invoices
  technician: 3,
  admin: 4,
};

/**
 * Load user's accessible companies into company store
 */
const loadUserCompanies = async () => {
  try {
    const response = await api.get('/auth/me/companies');
    const { companies, hasMultipleCompanies } = response.data;

    const companyStore = useCompanyStore.getState();
    companyStore.setCompanies(companies);

    // If user only has one company, select it by default
    if (companies.length === 1) {
      companyStore.setSelectedCompanyId(companies[0].id);
    } else if (hasMultipleCompanies) {
      // If they have the selected company persisted but it's no longer accessible, clear it
      const selectedId = companyStore.selectedCompanyId;
      if (selectedId && !companies.some(c => c.id === selectedId)) {
        companyStore.clearSelection();
      }
    }
  } catch (error) {
    console.error('Failed to load user companies:', error);
    // Don't fail auth if companies can't be loaded
  }
};

// Fallback timeout to prevent app from being stuck if rehydration fails
let initializationTimeout = null;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,  // Only true during initial auth check
      error: null,

      /**
       * Get user's current role
       */
      getRole: () => {
        return get().user?.role || 'end_user';
      },

      /**
       * Check if user is internal staff (the MSP's own people)
       */
      getIsInternalStaff: () => {
        return get().user?.isInternalStaff || false;
      },

      /**
       * Does a customer's role grant `resource: action`? The same rule the
       * server enforces (backend services/customerAccess.js), read from the
       * permissions the login response carries. For tickets and projects,
       * 'read' is true for either scope ('read' = company, 'read_own' = own).
       * Staff: true, their own checks apply. This only decides what to show;
       * the server refuses regardless.
       *
       * A customer may hold a different role at each company they can reach
       * (`/auth/me/companies` carries each company's permissions). With a
       * `companyId` the answer is that company's; without one it is "any
       * company grants it", which is what the navigation and a combined
       * "All companies" view need.
       */
      can: (resource, action, companyId = null) => {
        const user = get().user;
        if (!user) return false;
        if (user.isInternalStaff || isInternalRole(user.role)) return true;
        const allows = (permissions) => {
          const actions = permissions?.[resource];
          if (!Array.isArray(actions)) return false;
          if (action === 'read' && (resource === 'tickets' || resource === 'projects')) {
            return actions.includes('read') || actions.includes('read_own');
          }
          return actions.includes(action);
        };
        const companies = useCompanyStore.getState().companies || [];
        if (companyId != null) {
          const company = companies.find((c) => c.id === Number(companyId));
          // Before the companies load, or for the primary company, the
          // login response's permissions are that company's.
          return allows(company?.permissions || (Number(companyId) === user.cwCompanyId ? user.permissions : null));
        }
        return allows(user.permissions) || companies.some((c) => allows(c.permissions));
      },

      /**
       * Check if user has at least the specified role (hierarchy check)
       */
      hasMinRole: (minRole) => {
        const userRole = get().user?.role || 'end_user';
        const userLevel = ROLE_HIERARCHY[userRole] || 0;
        const requiredLevel = ROLE_HIERARCHY[minRole] || 0;
        return userLevel >= requiredLevel;
      },

      /**
       * Check if user can access admin features
       */
      canAccessAdmin: () => {
        const role = get().user?.role;
        return role === 'admin' || role === 'technician' || role === 'finance';
      },
      
      /**
       * Initialize auth state (check if already logged in)
       */
      initialize: async () => {
        try {
          const { accessToken, refreshToken } = get();

          if (!accessToken) {
            // No access token - clear any stale state and finish
            api.setAuthToken(null);
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
              isInitializing: false,
            });
            return;
          }

          try {
            api.setAuthToken(accessToken);
            const response = await api.get('/auth/me');
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
              isInitializing: false,
              error: null,
            });

            // Load user's accessible companies
            await loadUserCompanies();
          } catch (error) {
            // Token might be expired, try refresh
            if (refreshToken) {
              try {
                await get().refreshSession();
                set({ isInitializing: false });
              } catch (refreshError) {
                console.warn('Session refresh failed, logging out:', refreshError?.message);
                // Clear everything and redirect to login
                api.setAuthToken(null);
                set({
                  user: null,
                  accessToken: null,
                  refreshToken: null,
                  isAuthenticated: false,
                  isLoading: false,
                  isInitializing: false,
                  error: null,
                });
              }
            } else {
              // No refresh token, clear state
              api.setAuthToken(null);
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                isInitializing: false,
                error: null,
              });
            }
          }
        } catch (unexpectedError) {
          // Catch-all to ensure isInitializing is always set to false
          console.error('Unexpected error during auth initialization:', unexpectedError);
          api.setAuthToken(null);
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            isInitializing: false,
            error: null,
          });
        }
      },
      
      /**
       * Login with email/password
       */
      login: async (email, password) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, accessToken, refreshToken } = response.data;

          api.setAuthToken(accessToken);

          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Load user's accessible companies
          await loadUserCompanies();

          return { success: true };
        } catch (error) {
          let message = 'Login failed. Please try again.';

          if (error.response?.data?.error) {
            message = error.response.data.error;
          } else if (error.code === 'ERR_NETWORK' || !error.response) {
            message = 'Unable to connect to the server. Please check your internet connection and try again.';
          } else if (error.response?.status === 500) {
            message = 'Server error. Please try again later.';
          } else if (error.response?.status === 429) {
            message = 'Too many login attempts. Please wait a few minutes and try again.';
          }

          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },
      
      /**
       * Register new user
       */
      register: async (data) => {
        set({ error: null });

        try {
          const response = await api.post('/auth/register', data);
          const { user, accessToken, refreshToken } = response.data;

          api.setAuthToken(accessToken);

          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.error || 'Registration failed';
          set({ error: message });
          return { success: false, error: message };
        }
      },
      
      /**
       * Logout
       */
      logout: async () => {
        // One logout at a time: several requests failing together each ask for one.
        if (logoutInFlight) return logoutInFlight;
        logoutInFlight = (async () => {
          try {
            await api.post('/auth/logout');
          } catch {
            // Ignore logout errors (an expired session's logout is a 401)
          }
          get().clearSession();
        })().finally(() => { logoutInFlight = null; });
        return logoutInFlight;
      },

      clearSession: () => {
        api.setAuthToken(null);

        // Clear all cached data to prevent data leakage between users
        queryClient.clear();

        // Reset company store
        useCompanyStore.getState().reset();

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isInitializing: false,
          error: null,
        });
      },
      
      /**
       * Refresh access token
       */
      refreshSession: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

          api.setAuthToken(newAccessToken);

          set({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });

          // Re-fetch user data
          const userResponse = await api.get('/auth/me');
          set({
            user: userResponse.data.user,
            isAuthenticated: true,
            isLoading: false,
          });

          // Re-load user's accessible companies
          await loadUserCompanies();
        } catch (error) {
          // Clear tokens on refresh failure to prevent stale state
          api.setAuthToken(null);
          set({
            accessToken: null,
            refreshToken: null,
          });
          throw error;
        }
      },
      
      /**
       * Handle SSO callback (tokens from URL)
       */
      handleSSOCallback: async (accessToken, refreshToken) => {
        api.setAuthToken(accessToken);

        try {
          const response = await api.get('/auth/me');

          set({
            user: response.data.user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Load user's accessible companies
          await loadUserCompanies();

          return { success: true };
        } catch (error) {
          set({ isLoading: false, error: 'SSO authentication failed' });
          return { success: false };
        }
      },
      
      /**
       * Read the signed-in user's role, permissions and companies again after
       * an admin saved a change to them. A failed read changes nothing.
       */
      refreshAccount: async () => {
        if (!get().isAuthenticated) return;
        try {
          const response = await api.get('/auth/me');
          if (response.data?.user) set({ user: response.data.user });
          await loadUserCompanies();
        } catch (error) {
          console.warn('The signed-in account could not be refreshed:', error.message);
        }
      },

      /**
       * Update user data in store
       */
      updateUser: (userData) => {
        set({ user: { ...get().user, ...userData } });
      },
      
      /**
       * Clear error
       */
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'halomts-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => {
        // Set a fallback timeout in case rehydration hangs (mobile browsers)
        initializationTimeout = setTimeout(() => {
          const store = useAuthStore.getState();
          if (store.isInitializing) {
            console.warn('Auth rehydration timeout - forcing initialization');
            store.initialize().catch(() => {
              // If initialize fails, just clear the initializing state
              useAuthStore.setState({ isInitializing: false, isLoading: false });
            });
          }
        }, 3000); // 3 second fallback

        // This callback is called AFTER the persisted state is restored
        return (_state, error) => {
          // Clear the fallback timeout since rehydration completed
          if (initializationTimeout) {
            clearTimeout(initializationTimeout);
            initializationTimeout = null;
          }

          if (error) {
            console.error('Failed to rehydrate auth state:', error);
          }
          // Defer initialization to next tick to avoid circular dependency
          setTimeout(() => {
            const store = useAuthStore.getState();
            if (error) {
              store.logout();
            } else {
              store.initialize();
            }
          }, 0);
        };
      },
    }
  )
);

export default useAuthStore;
